import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { geoFrance, professionalProfiles, professionalCategories, serviceCategories } from '@afficheservices/db/schema'
import { eq, sql, and } from 'drizzle-orm'
import { assertCityNotDomTom } from '~/lib/dom-tom.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema, CityPlaceSchema, FAQSchema } from '~/components/structured-data'
import { CityMap } from '~/components/city-map'
import { CrossLinksHub } from '~/components/cross-links'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { TASKS_BY_SERVICE, DEAD_TASK_SLUGS } from '~/lib/tarifs-tasks-data'
import { SERVICE_LABELS, artisanSlug } from '~/lib/programmatic-seo'
import {
  CLIMATE_NOTES,
  REGULATIONS_BY_DEPT,
  DEFAULT_REGULATION_NOTE,
  DEPT_FACTS,
  DEFAULT_DEPT_FACT,
  formatFr,
  formatResponseTime,
} from '~/lib/page-variants'
import { getNeighborCities } from '~/lib/city-data.server'
import { fetchCityServiceStats } from '~/lib/city-service-stats.server'
import { wikidataUrlForInsee, communeImageForInsee } from '~/lib/commune-wikidata.server'
import { isPathIndexable } from '~/lib/index-whitelist.server'
import { getProgrammaticContent } from '~/lib/programmatic-content.server'
import { ArrowLeft, ArrowRight, Euro, CheckCircle } from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

export async function loader({ params }: LoaderFunctionArgs) {
  const svcSlug = params.service ?? ''
  const taskSlug = params.task ?? ''
  const citySlug = params.city ?? ''

  const svc = SERVICE_LABELS[svcSlug]
  const tasks = TASKS_BY_SERVICE[svcSlug]
  const task = tasks?.find((t) => t.slug === taskSlug)
  if (!svc || !task) throw new Response('Not Found', { status: 404 })
  // Zero-demand task nationwide (GSC audit 2026-07) — 410 Gone so Google drops
  // it permanently instead of re-crawling ~2k dead URLs per task.
  if (DEAD_TASK_SLUGS.has(taskSlug)) throw new Response('Gone', { status: 410 })

  const cityRows = await db
    .select({ name: geoFrance.communeName, postalCode: geoFrance.postalCode, dept: geoFrance.departmentCode, deptName: geoFrance.departmentName, region: geoFrance.regionName, population: geoFrance.population, lat: geoFrance.latitude, lon: geoFrance.longitude, communeCode: geoFrance.communeCode })
    .from(geoFrance)
    .where(sql`LOWER(REPLACE(REPLACE(TRANSLATE(${geoFrance.communeName},'àáâãäåèéêëìíîïòóôõöùúûüýÿñçÀÁÂÃÄÅÈÉÊËÌÍÎÏÒÓÔÕÖÙÚÛÜÝŸÑÇ','aaaaaaeeeeiiiioooooouuuuyyncAAAAAAEEEEIIIIOOOOOUUUUYYNC'),' ','-'),'''','-'))=${citySlug}`)
    .limit(1)
  if (cityRows.length === 0) throw new Response('Ville non trouvée', { status: 404 })
  const city = cityRows[0]
  // 410 only if every namesake is DOM-TOM (keeps mainland Saint-Denis etc.).
  await assertCityNotDomTom(citySlug)

  // Pro count + top 3 pros in this department for the service category.
  // Names + count diverge every city — guards against duplicate-content
  // penalty across programmatic /tarifs/{svc}/{city}/{task} URLs.
  const [catRow] = await db.select({ id: serviceCategories.id }).from(serviceCategories).where(eq(serviceCategories.slug, svcSlug)).limit(1)
  let proCount = 0
  let topPros: Array<{ id: string; companyName: string; slug: string; city: string; ratingAvg: string | null; reviewCount: number }> = []
  if (catRow) {
    const [row] = await db.select({ count: sql<number>`count(DISTINCT ${professionalProfiles.id})::int` })
      .from(professionalProfiles)
      .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
      .where(and(eq(professionalCategories.categoryId, catRow.id), eq(professionalProfiles.departmentCode, city.dept), eq(professionalProfiles.isDemo, false)))
    proCount = Number(row?.count ?? 0)

    if (proCount > 0) {
      topPros = await db
        .select({
          id: professionalProfiles.id,
          companyName: professionalProfiles.companyName,
          slug: professionalProfiles.slug,
          city: professionalProfiles.city,
          ratingAvg: professionalProfiles.ratingAvg,
          reviewCount: professionalProfiles.reviewCount,
        })
        .from(professionalProfiles)
        .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
        .where(and(eq(professionalCategories.categoryId, catRow.id), eq(professionalProfiles.departmentCode, city.dept), eq(professionalProfiles.isDemo, false)))
        .orderBy(sql`${professionalProfiles.reviewCount} DESC NULLS LAST, ${professionalProfiles.companyName}`)
        .limit(3)
    }
  }

  const otherTasks = tasks.filter((t) => t.slug !== taskSlug && !DEAD_TASK_SLUGS.has(t.slug)).slice(0, 5)

  // Per-city signals — neighbors + activity for content uniqueness.
  const lat = Number(city.lat)
  const lon = Number(city.lon)
  const [neighbors, stats] = await Promise.all([
    getNeighborCities(lat, lon, city.communeCode, 5, 30),
    catRow ? fetchCityServiceStats(catRow.id, city.name) : Promise.resolve(null),
  ])

  return {
    svc, svcSlug, task, city, citySlug, otherTasks, proCount, topPros, neighbors, stats,
    // index only GSC-proven URLs; never-ranked tail stays noindex.
    indexable: await isPathIndexable(`/tarifs/${svcSlug}/${citySlug}/${task.slug}`),
    // Phase-2 per-city AI override (demand-head only); null → template.
    aiContent: await getProgrammaticContent({ routeType: 'tarifs', slug: svcSlug, citySlug, taskSlug: task.slug }),
    wikidataUrl: wikidataUrlForInsee(city.communeCode),
    communeImageUrl: communeImageForInsee(city.communeCode),
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return []
  const { svc, task, city, svcSlug, citySlug } = data
  // Drop the " | AfficheServices" suffix (Google appends the brand) — fits the
  // SERP without truncation (DataForSEO: title_too_long).
  const title = data.aiContent?.metaTitle ?? `${task.label} à ${city.name} — Tarif ${svc.artisan} ${task.priceRange}`
  const rawDesc = data.aiContent?.metaDescription ?? `Prix ${task.label.toLowerCase()} à ${city.name} (${city.postalCode}) : ${task.priceRange}. Comparez 3 devis gratuits de ${svc.artisan}s vérifiés.`
  // Cap at 160 on a word boundary (DataForSEO: description_length was ~260).
  const desc = rawDesc.length <= 160 ? rawDesc : rawDesc.slice(0, 157).replace(/\s+\S*$/, '') + '…'
  return [
    { title }, { name: 'description', content: desc },
    // Conditional index: only GSC-proven URLs (Google already ranks them).
    // The never-ranked tail stays noindex,follow to avoid re-introducing the
    // near-duplicate doorway matrix. Whitelist refreshed weekly from GSC.
    { name: 'robots', content: data.indexable ? 'index, follow' : 'noindex, follow' },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/tarifs/${svcSlug}/${citySlug}/${task.slug}` },
    { property: 'og:title', content: title }, { property: 'og:description', content: desc },
    { property: 'og:image', content: `https://www.afficheservices.fr/og/tarifs/${svcSlug}/${citySlug}.svg` },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:type', content: 'image/svg+xml' },
  ]
}

export default function TarifsTaskCityPage() {
  const { svc, svcSlug, task, city, citySlug, otherTasks, proCount, topPros, neighbors, stats, wikidataUrl, communeImageUrl, aiContent } = useLoaderData<typeof loader>()
  // Grounded per-city FAQ + expert tips (rendered only when generated — this
  // route has no template FAQ).
  const aiFaq = aiContent?.faqItems ?? []
  const aiTips = aiContent?.expertTips ?? []
  // CTA intent-match: tiny jobs (e.g. key copy, max ~<150 €) don't warrant a
  // 3-quote funnel — the searcher wants one nearby pro now, so route them to
  // the local directory. Bigger jobs keep the devis funnel where comparing
  // multiple quotes actually pays off. Upper bound parsed from priceRange
  // ("800 – 1 500 €" → 1500).
  const priceMax = parseInt((task.priceRange.split(/[–-]/).pop() ?? '').replace(/[^\d]/g, ''), 10) || 0
  const lowTicket = priceMax > 0 && priceMax < 150
  const climate = CLIMATE_NOTES[city.region]
  const regulation = REGULATIONS_BY_DEPT[city.dept] ?? DEFAULT_REGULATION_NOTE
  const deptFact = DEPT_FACTS[city.dept] ?? DEFAULT_DEPT_FACT
  const responseTime = stats ? formatResponseTime(stats.avgResponseHours) : null
  // Population label varies the page text per city — guards against
  // 0% uniqueness across same-task-different-city programmatic pages.
  const popLabel = city.population
    ? city.population > 100000 ? `grande ville de ${(city.population / 1000).toFixed(0)}k habitants`
    : city.population > 20000 ? `commune de ${(city.population / 1000).toFixed(0)}k habitants`
    : `commune de ${formatFr(city.population)} habitants`
    : `commune du ${city.deptName}`

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: `Tarifs ${svc.artisan}`, url: `https://www.afficheservices.fr/tarifs/${artisanSlug(svcSlug)}/${citySlug}` },
        { name: task.label, url: `https://www.afficheservices.fr/tarifs/${svcSlug}/${citySlug}/${task.slug}` },
      ]} />
      <CityPlaceSchema
        city={{
          name: city.name,
          postalCode: city.postalCode,
          dept: city.dept,
          deptName: city.deptName,
          region: city.region,
          lat: city.lat,
          lon: city.lon,
        }}
        service={{ name: `${task.label} (${svc.name})`, slug: svcSlug }}
        canonicalUrl={`https://www.afficheservices.fr/tarifs/${svcSlug}/${citySlug}/${task.slug}`}
        priceBand={stats?.priceBand}
        offerCount={proCount}
        wikidataUrl={wikidataUrl}
        communeImageUrl={communeImageUrl}
      />

      <section className="bg-gradient-to-br from-amber-900 via-orange-900 to-slate-900 px-4 py-12 text-white sm:py-16">
        <div className="mx-auto max-w-4xl">
          <Link to={`/tarifs/${artisanSlug(svcSlug)}/${citySlug}`} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-200 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Tarifs {svc.artisan} à {city.name}
          </Link>
          <h1 className="mt-4 text-3xl font-extrabold sm:text-4xl">
            {task.label} à {city.name}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-amber-200">
            {task.desc} Prix moyen à {city.name} ({city.postalCode}, {city.deptName}).
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-xl bg-white/10 px-5 py-3 text-xl font-bold text-white ring-1 ring-white/20">
            <Euro className="h-5 w-5" /> {task.priceRange}
          </div>
          {proCount > 0 && (
            <div className="mt-3 flex items-center gap-1.5 text-sm font-medium text-amber-100">
              <CheckCircle className="h-4 w-4 text-emerald-300" />
              {proCount} {svc.artisan}{proCount > 1 ? 's' : ''} vérifié{proCount > 1 ? 's' : ''} disponible{proCount > 1 ? 's' : ''} à {city.name}
            </div>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-12 lg:px-6">
        <div className="mb-8">
          <CityMap lat={Number(city.lat)} lon={Number(city.lon)} cityName={city.name} height={240} />
        </div>

        <section>
          <h2 className="text-xl font-bold text-slate-900">Prix {task.label.toLowerCase()} à {city.name}</h2>
          {aiContent?.answerBlock && (
            <div className="mt-3 rounded-xl border-l-4 border-primary bg-slate-50 p-4 text-sm leading-relaxed text-slate-800">
              {aiContent.answerBlock}
            </div>
          )}
          <p className="mt-3 text-sm leading-relaxed text-slate-700">
            Le tarif pour {task.label.toLowerCase()} à {city.name} varie de <strong>{task.priceRange}</strong> en 2026.
            Ce prix dépend de la complexité de l’intervention, de l’accessibilité, des matériaux utilisés,
            et du tarif horaire du {svc.artisan} ({city.deptName}). Pour obtenir un prix exact, comparez
            3 devis gratuits.
          </p>
          {/* Phase-2 unique per-city prose replaces the template line when present. */}
          {aiContent?.localContext ? (
            <p className="mt-3 text-sm leading-relaxed text-slate-700">{aiContent.localContext}</p>
          ) : (
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              {city.name} ({city.postalCode}) est une {popLabel} dans le {city.deptName} ({city.dept}), région {city.region}.
              {proCount > 0
                ? ` ${proCount} ${svc.artisan}s y interviennent — les tarifs reflètent la concurrence locale et le coût horaire moyen du département.`
                : ` Les ${svc.artisan}s du ${city.deptName} pratiquent les tarifs habituels de la région.`}
            </p>
          )}
          {climate && (
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              <strong>Spécificité régionale :</strong> {climate}
            </p>
          )}
        </section>

        {stats && stats.recentLeads > 0 && (
          <section className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <p className="text-sm font-semibold text-emerald-900">
              📈 Tarifs demandés {city.deptName} ({city.dept}) sur 30 jours
            </p>
            <p className="mt-1 text-sm text-emerald-800">
              <strong>{formatFr(stats.recentLeads)} devis</strong>
              {responseTime ? ` — réponse moyenne ${responseTime}` : ''}
              {stats.priceBand
                ? ` · médiane ${formatFr(stats.priceBand.median)} € (de ${formatFr(stats.priceBand.min)} à ${formatFr(stats.priceBand.max)} €)`
                : stats.proCount > 0
                  ? ` · ${stats.proCount} artisans actifs`
                  : ''}
              .
            </p>
          </section>
        )}

        <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">
            ⚖️ Réglementation locale ({city.dept})
          </p>
          <p className="mt-1 text-sm leading-relaxed text-amber-800">{regulation}</p>
        </section>

        <section className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-900">
            📊 Le saviez-vous ? ({city.deptName})
          </p>
          <p className="mt-1 text-sm leading-relaxed text-slate-700">{deptFact}</p>
        </section>

        {topPros.length > 0 && (
          <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <h2 className="text-lg font-bold text-amber-900">
              Top {topPros.length} {svc.artisan}{topPros.length > 1 ? 's' : ''} à {city.name} et {city.deptName}
            </h2>
            <ul className="mt-4 space-y-2">
              {topPros.map((pro, i) => (
                <li key={pro.id} className="flex items-start gap-3 text-sm">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-700 text-xs font-bold text-white">{i + 1}</span>
                  <div className="flex-1">
                    <Link to={`/pro/${pro.slug}`} className="font-semibold text-amber-900 hover:text-amber-700">
                      {pro.companyName}
                    </Link>
                    <span className="ml-2 text-xs text-amber-700/70">— {pro.city}</span>
                    {Number(pro.reviewCount) > 0 && pro.ratingAvg && (
                      <span className="ml-2 text-xs text-amber-600">★ {pro.ratingAvg} ({pro.reviewCount} avis)</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-amber-700/70">
              Devis détaillé sur demande — comparaison libre, sans engagement.
            </p>
          </section>
        )}

        <section className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <h2 className="text-lg font-bold text-amber-900">Ce qui influence le prix</h2>
          <ul className="mt-4 space-y-2">
            {[
              `Tarif horaire du ${svc.artisan} à ${city.name} (zone ${city.deptName})`,
              'Complexité et durée de l\'intervention',
              'Coût des matériaux et pièces détachées',
              'Accessibilité du chantier (étage, stationnement)',
              'Urgence de l\'intervention (+ 30-50 % en soirée/week-end)',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-amber-800">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {item}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-8">
          <h2 className="text-lg font-bold text-slate-900">Comment économiser ?</h2>
          <ul className="mt-4 space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Comparez au moins 3 devis (économie 20-30 % typique)</li>
            <li className="flex items-start gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Évitez les interventions d'urgence si possible</li>
            <li className="flex items-start gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Vérifiez les aides (CEE, MaPrimeRénov') pour les travaux énergétiques</li>
            <li className="flex items-start gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Demandez un devis détaillé (main-d'œuvre vs fournitures)</li>
          </ul>
        </section>

        {aiFaq.length > 0 && <FAQSchema items={aiFaq.map((f) => ({ question: f.q, answer: f.a }))} />}

        {aiTips.length > 0 && (
          <section className="mt-8">
            <h2 className="text-lg font-bold text-slate-900">Conseils d'expert à {city.name}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {aiTips.map((tip, i) => (
                <div key={i} className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
                  <p className="text-sm font-semibold text-emerald-900">{tip.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{tip.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {aiFaq.length > 0 && (
          <section className="mt-8">
            <h2 className="text-lg font-bold text-slate-900">Questions fréquentes — {task.label} à {city.name}</h2>
            <div className="mt-4 space-y-3">
              {aiFaq.map((item, i) => (
                <details key={i} className="group rounded-xl border border-border bg-white p-4">
                  <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900">
                    {item.q}
                    <ArrowRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {otherTasks.length > 0 && (
          <section className="mt-10">
            <h2 className="text-lg font-bold text-slate-900">Autres tarifs {svc.artisan} à {city.name}</h2>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {otherTasks.map((t) => (
                <Link
                  key={t.slug}
                  to={`/tarifs/${svcSlug}/${citySlug}/${t.slug}`}
                  className="group flex items-center justify-between rounded-xl border border-border bg-white p-4 hover:border-primary"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-900 group-hover:text-primary">{t.label}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{t.priceRange}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-primary" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10">
          <CrossLinksHub
            primarySlug={svcSlug}
            cityName={city.name}
            citySlug={citySlug}
            currentRoute="tarifs"
          />
        </div>

        {neighbors.length > 0 && (
          <section className="mt-10">
            <h2 className="text-lg font-bold text-slate-900">{task.label} dans les communes voisines</h2>
            <p className="mt-2 text-sm text-slate-600">
              Mêmes tarifs constatés dans un rayon de 30 km autour de {city.name} :
            </p>
            <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {neighbors.map((n) => (
                <li key={n.postalCode + n.name}>
                  <Link
                    to={`/tarifs/${svcSlug}/${n.name.toLowerCase().replace(/['\s]/g, '-')}/${task.slug}`}
                    className="block rounded-lg border border-slate-200 bg-white p-2.5 text-sm hover:border-primary hover:bg-slate-50"
                  >
                    <span className="font-medium text-slate-900">{n.name}</span>
                    <span className="ml-1 text-xs text-slate-500">({n.km.toFixed(0)} km)</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-10 text-center">
          {lowTicket ? (
            <Link to={`/services/${svcSlug}/${citySlug}`} className="inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3.5 text-sm font-bold text-white hover:bg-primary-hover">
              Trouver un {svc.artisan} à {city.name} <ArrowRight className="h-4 w-4" />
            </Link>
          ) : (
            <Link to="/devis-gratuit" className="inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3.5 text-sm font-bold text-white hover:bg-primary-hover">
              Comparer 3 devis gratuits <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </section>
      </div>

      <Footer />
    </div>
  )
}
